/**
 * Produces a stable string signature for a data row, used for diff comparison.
 * The `_rowId` internal key and hidden columns (softwareId) are excluded.
 */
export function rowSignature(row) {
  return Object.keys(row)
    .filter((k) => k !== "_rowId" && k !== "softwareId")
    .sort()
    .map((k) => `${k}:${String(row[k] ?? "").trim()}`)
    .join("|");
}

/**
 * Compares two row dictionaries and returns which rows are unique to each side.
 * Returns { leftMap, rightMap } where each map is { rowId: true } for diffed rows.
 */
export function buildRowExistenceDiff(leftDict, rightDict) {
  const leftRows = Object.entries(leftDict || {}).map(([id, row]) => ({
    id,
    sig: rowSignature(row),
  }));
  const rightRows = Object.entries(rightDict || {}).map(([id, row]) => ({
    id,
    sig: rowSignature(row),
  }));

  const rightSigSet = new Set(rightRows.map((r) => r.sig));
  const leftSigSet = new Set(leftRows.map((r) => r.sig));

  const leftMap = {};
  const rightMap = {};

  leftRows.forEach(({ id, sig }) => {
    if (!rightSigSet.has(sig)) leftMap[id] = true;
  });
  rightRows.forEach(({ id, sig }) => {
    if (!leftSigSet.has(sig)) rightMap[id] = true;
  });

  return { leftMap, rightMap };
}

// ── Column definitions ────────────────────────────────────────────────────────

/** Keys never rendered as visible columns */
const HIDDEN_KEYS = new Set(["_rowId", "softwareId"]);

/**
 * Tagged mode: show only tagNumber / materialCode (case-insensitive key match).
 * softwareId is stored in data but never shown.
 */
const TAGGED_VISIBLE = ["tagnumber", "tagNumber", "tag_number", "materialcode", "materialCode", "material_code"];
const TAGGED_LABEL = {
  tagnumber: "Tag Number",
  tagNumber: "Tag Number",
  tag_number: "Tag Number",
  materialcode: "Material Code",
  materialCode: "Material Code",
  material_code: "Material Code",
};

/**
 * Bulk mode: show item, description, quantity, dimension, unit, weight.
 * Key matching is loose (lower-case contains).
 */
const BULK_KEY_ORDER = ["item", "description", "quantity", "dimension", "unit", "weight"];
const BULK_LABEL = {
  item: "Item",
  description: "Description",
  quantity: "Qty",
  dimension: "Dimension",
  unit: "Unit",
  weight: "Weight",
};

function normalize(key) {
  return key.toLowerCase().replace(/[_\s-]/g, "");
}

/**
 * Returns MRT column definitions filtered and ordered for the given mode.
 * Falls back to all non-hidden keys if no recognised keys are found.
 */
export function getColumnsFromDict(dict, mode = "bulk") {
  const firstRow = Object.values(dict || {})[0];
  if (!firstRow) return [];

  const rowKeys = Object.keys(firstRow).filter((k) => !HIDDEN_KEYS.has(k));

  if (mode === "tagged") {
    // Match row keys against tagged visible set
    const matched = rowKeys.filter((k) =>
      TAGGED_VISIBLE.some((v) => normalize(v) === normalize(k))
    );
    const ordered = matched.length > 0 ? matched : rowKeys;
    return ordered.map((key) => ({
      accessorKey: key,
      header: TAGGED_LABEL[key] ?? friendlyHeader(key),
      size: 160,
      enableEditing: false,
    }));
  }

  // Bulk: map each desired column to the actual key in the row
  const result = [];
  BULK_KEY_ORDER.forEach((target) => {
    const match = rowKeys.find((k) => normalize(k).includes(normalize(target)));
    if (match) {
      result.push({
        accessorKey: match,
        header: BULK_LABEL[target],
        size: target === "description" ? 200 : 100,
        enableEditing: false,
      });
    }
  });

  // Fallback: if nothing matched, show all non-hidden keys
  return result.length > 0
    ? result
    : rowKeys.map((key) => ({
        accessorKey: key,
        header: friendlyHeader(key),
        size: 120,
        enableEditing: false,
      }));
}

function friendlyHeader(key) {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim();
}

/**
 * Converts a row-keyed dictionary to the array format MRT expects.
 */
export function dictToArray(dict) {
  return Object.entries(dict || {}).map(([id, row]) => ({
    _rowId: id,
    ...row,
  }));
}
